// Field schemas for the admin CMS.
// types: text | textarea | image | bool | stringList | pairs

export const COLLECTION_SCHEMAS = {
  editions: { titleKey: 'title', subKey: 'month', fields: [
    { key: 'month', label: 'Month (e.g. September 2026)' },
    { key: 'title', label: 'Issue title' },
    { key: 'cover', label: 'Cover image URL', type: 'image' },
    { key: 'pdfUrl', label: 'Upload magazine PDF (in-app flip reader)', type: 'pdf' },
    { key: 'readUrl', label: 'OR external flipbook URL (FlipHTML5 link) — optional' },
    { key: 'summary', label: 'Summary', type: 'textarea' },
    { key: 'latest', label: 'Mark as latest issue', type: 'bool' },
  ]},
  articles: { titleKey: 'title', subKey: 'category', fields: [
    { key: 'title' }, { key: 'slug', label: 'Slug (url id)' }, { key: 'category' },
    { key: 'excerpt', type: 'textarea' }, { key: 'image', type: 'image' },
    { key: 'author' }, { key: 'role' }, { key: 'date' }, { key: 'readTime' },
    { key: 'featured', type: 'bool' }, { key: 'body', label: 'Body paragraphs', type: 'stringList' },
  ]},
  authored: { titleKey: 'title', subKey: 'author', fields: [
    { key: 'title' }, { key: 'slug', label: 'Links to article slug' }, { key: 'category' },
    { key: 'author' }, { key: 'role' }, { key: 'readTime' }, { key: 'image', type: 'image' },
    { key: 'excerpt', type: 'textarea' },
  ]},
  podcasts: { titleKey: 'title', subKey: 'guest', fields: [
    { key: 'videoId', label: 'YouTube video ID' }, { key: 'title' }, { key: 'guest' },
    { key: 'description', type: 'textarea' },
  ]},
  spotlights: { titleKey: 'org', subKey: 'full', fields: [
    { key: 'org', label: 'Short name' }, { key: 'full', label: 'Full name' },
    { key: 'blurb', type: 'textarea' }, { key: 'accent', label: 'Accent colour (hex)' },
  ]},
  whatMadeNews: { titleKey: 'headline', subKey: 'tag', fields: [
    { key: 'headline' }, { key: 'tag' },
  ]},
  featuredEditions: { titleKey: 'title', subKey: 'month', fields: [
    { key: 'month' }, { key: 'title' }, { key: 'image', type: 'image' }, { key: 'teaser', type: 'textarea' },
  ]},
  upcomingCoverStories: { titleKey: 'title', subKey: 'month', fields: [
    { key: 'month' }, { key: 'title' }, { key: 'teaser', type: 'textarea' },
  ]},
  partnerEvents: { titleKey: 'title', subKey: 'partner', fields: [
    { key: 'title' }, { key: 'partner' }, { key: 'date' }, { key: 'location' }, { key: 'image', type: 'image' },
  ]},
  topics: { titleKey: 'name', subKey: 'slug', fields: [
    { key: 'name' }, { key: 'slug' }, { key: 'blurb', type: 'textarea' },
  ]},
  abcSpeakers: { titleKey: 'name', subKey: 'org', fields: [
    { key: 'name', label: 'Speaker name' }, { key: 'role', label: 'Designation' },
    { key: 'org', label: 'Organisation' }, { key: 'photo', label: 'Photo', type: 'image' },
  ]},
  abcSponsors: { titleKey: 'name', subKey: 'tier', fields: [
    { key: 'name', label: 'Sponsor / partner name' }, { key: 'tier', label: 'Tier (e.g. Association Partner)' },
    { key: 'logo', label: 'Logo', type: 'image' },
  ]},
  the100Leaders: { titleKey: 'name', subKey: 'organisation', fields: [
    { key: 'name', label: 'Leader name' }, { key: 'designation', label: 'Designation' },
    { key: 'organisation', label: 'Organisation' }, { key: 'sector', label: 'Sector (e.g. Solar, Gas, Power)' },
    { key: 'citation', label: 'One-line citation (optional)', type: 'textarea' },
    { key: 'image', label: 'Photo (optional)', type: 'image' },
  ]},
};

export const SINGLETON_SCHEMAS = {
  coverStory: [
    { key: 'edition' }, { key: 'eyebrow' }, { key: 'title' }, { key: 'dek', type: 'textarea' },
    { key: 'editor' }, { key: 'editorRole' }, { key: 'image', type: 'image' },
    { key: 'body', label: 'Body paragraphs', type: 'stringList' },
  ],
  industryReport: [
    { key: 'eyebrow' }, { key: 'title' }, { key: 'summary', type: 'textarea' }, { key: 'image', type: 'image' },
    { key: 'stats', label: 'Stats (value / label)', type: 'pairs' },
  ],
  enersiderBrief: [
    { key: 'eyebrow' }, { key: 'title' }, { key: 'points', label: 'Points', type: 'stringList' },
  ],
  enersiderView: [
    { key: 'eyebrow' }, { key: 'title' }, { key: 'author' }, { key: 'authorRole' },
    { key: 'image', label: 'Image (optional)', type: 'image' },
    { key: 'body', label: 'Body paragraphs', type: 'stringList' },
  ],
  newsmaker: [
    { key: 'eyebrow' }, { key: 'name' }, { key: 'designation' }, { key: 'org' }, { key: 'image', type: 'image' },
    { key: 'quote', type: 'textarea' }, { key: 'bio', type: 'textarea' },
  ],
  conclave: [
    { key: 'name' }, { key: 'tagline' }, { key: 'date' }, { key: 'location' }, { key: 'image', type: 'image' },
    { key: 'description', type: 'textarea' }, { key: 'stats', label: 'Stats (value / label)', type: 'pairs' },
    { key: 'reportPdf', label: 'Post-show report PDF (shown on the ABC page)', type: 'pdf' },
    { key: 'reportEdition', label: 'Report label (e.g. Edition 1 · March 2026)' },
    { key: 'nextEditionAt', label: 'Next edition start, India time — format YYYY-MM-DD HH:MM (e.g. 2027-03-23 10:00). Countdown shows when filled' },
    { key: 'nextEditionLabel', label: 'Countdown heading (default: Edition 2 begins in)' },
  ],
  felas: [
    { key: 'name' }, { key: 'full' }, { key: 'tagline' }, { key: 'date' }, { key: 'location' }, { key: 'image', type: 'image' },
    { key: 'description', type: 'textarea' }, { key: 'categories', label: 'Award categories', type: 'stringList' },
    { key: 'reportPdf', label: 'Post-show report PDF (shown on the FELAS page)', type: 'pdf' },
    { key: 'reportEdition', label: 'Report label (e.g. Edition 1 · March 2026)' },
    { key: 'nextEditionAt', label: 'Next edition start, India time — format YYYY-MM-DD HH:MM (e.g. 2027-03-23 10:00). Countdown shows when filled' },
    { key: 'nextEditionLabel', label: 'Countdown heading (default: Edition 2 begins in)' },
  ],
  iecl: [
    { key: 'eyebrow', label: 'Small heading above title' }, { key: 'title', label: 'Title' }, { key: 'tagline' },
    { key: 'description', type: 'textarea' }, { key: 'date' }, { key: 'location' },
    { key: 'logo', label: 'IECL logo', type: 'image' },
    { key: 'stats', label: 'Stats (number / label)', type: 'pairs' },
    { key: 'franchisesEyebrow', label: 'Franchises — small heading' }, { key: 'franchisesTitle', label: 'Franchises — title' },
    { key: 'franchises', label: 'Franchises (team name / sector)', type: 'pairs', ph: ['Team name', 'Sector'] },
    { key: 'franchisesNote', label: 'Franchises — footnote' },
    { key: 'formEyebrow', label: 'Interest form — small heading' }, { key: 'formTitle', label: 'Interest form — title' },
    { key: 'formIntro', label: 'Interest form — intro', type: 'textarea' },
    { key: 'interestOptions', label: '"Interested to" dropdown options', type: 'stringList' },
  ],
  the100: [
    { key: 'eyebrow', label: 'Small heading above title' }, { key: 'subtitle', label: 'Subtitle' },
    { key: 'description', type: 'textarea' }, { key: 'cover', label: 'Cover image', type: 'image' },
    { key: 'highlights', label: 'Highlights (3 short lines)', type: 'stringList' },
    { key: 'pillars', label: 'Pillars (heading / description)', type: 'pairs', ph: ['Heading', 'Description'] },
    { key: 'formEyebrow', label: 'Nomination form — small heading' }, { key: 'formTitle', label: 'Nomination form — title' },
    { key: 'formIntro', label: 'Nomination form — intro', type: 'textarea' },
    { key: 'sectors', label: 'Sector dropdown options', type: 'stringList' },
  ],
  settings: [
    { key: 'digitalEditionUrl', label: 'Default digital edition URL' },
    { key: 'editionLabel', label: 'Current edition label' },
  ],
};

export const VIEWS = [
  { group: 'Overview', items: [
    { key: 'dashboard', label: 'Dashboard', type: 'dashboard' },
    { key: 'wire', label: 'The Wire', type: 'wire' },
    { key: 'sectorRules', label: 'Sector Rules', type: 'sectorRules' },
  ]},
  { group: 'Magazine', items: [
    { key: 'editions', label: 'Editions (E-Magazine)', type: 'collection', name: 'editions' },
    { key: 'coverStory', label: 'Cover Story', type: 'singleton', name: 'coverStory' },
    { key: 'authored', label: 'Authored Articles', type: 'collection', name: 'authored' },
    { key: 'spotlights', label: 'Spotlight', type: 'collection', name: 'spotlights' },
    { key: 'whatMadeNews', label: 'What Made News', type: 'collection', name: 'whatMadeNews' },
    { key: 'industryReport', label: 'Industry Report', type: 'singleton', name: 'industryReport' },
    { key: 'enersiderBrief', label: 'Enersider Brief', type: 'singleton', name: 'enersiderBrief' },
    { key: 'enersiderView', label: 'Enersider View', type: 'singleton', name: 'enersiderView' },
    { key: 'newsmaker', label: 'Newsmaker of the Month', type: 'singleton', name: 'newsmaker' },
    { key: 'featuredEditions', label: 'Featured', type: 'collection', name: 'featuredEditions' },
    { key: 'upcomingCoverStories', label: 'Upcoming Cover Stories', type: 'collection', name: 'upcomingCoverStories' },
  ]},
  { group: 'Site', items: [
    { key: 'articles', label: 'Articles (Home/News)', type: 'collection', name: 'articles' },
    { key: 'podcasts', label: 'Podcasts', type: 'collection', name: 'podcasts' },
    { key: 'topics', label: 'Topics', type: 'collection', name: 'topics' },
  ]},
  { group: 'Events', items: [
    { key: 'conclave', label: 'ABC — Overview', type: 'singleton', name: 'conclave' },
    { key: 'abcSpeakers', label: 'ABC — Speakers', type: 'collection', name: 'abcSpeakers' },
    { key: 'abcSponsors', label: 'ABC — Sponsors', type: 'collection', name: 'abcSponsors' },
    { key: 'felas', label: 'FELAS Awards', type: 'singleton', name: 'felas' },
    { key: 'partnerEvents', label: 'Partner Events', type: 'collection', name: 'partnerEvents' },
    { key: 'iecl', label: 'IECL — Cricket League', type: 'singleton', name: 'iecl' },
    { key: 'the100', label: 'The100', type: 'singleton', name: 'the100' },
    { key: 'the100Leaders', label: 'The100 — Leaders List', type: 'collection', name: 'the100Leaders' },
  ]},
  { group: 'Admin', items: [
    { key: 'leads', label: 'Form Submissions', type: 'leads' },
    { key: 'settings', label: 'Settings', type: 'singleton', name: 'settings' },
    { key: 'users', label: 'Admin Users', type: 'users' },
  ]},
];
